import axios from 'axios';

// Detect if running in Capacitor (native app)
const isCapacitor = (): boolean => {
  return typeof window !== 'undefined' && 
         (window as any).Capacitor !== undefined;
};

// API URL configuration:
// 1. VITE_API_URL env var (set at build time)
// 2. For Capacitor/mobile: MUST use full URL to your server
// 3. For web: auto-detect from browser location (same origin, no port for standard ports)
const getApiUrl = (): string => {
  // If explicitly set via environment variable at build time, use it
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // For Capacitor (mobile app), we need a hardcoded server URL
  // because there's no browser location to detect from
  if (isCapacitor()) {
    // IMPORTANT: Change this to your production server URL
    console.warn('[API Client] Running in Capacitor - using default API URL. Set VITE_API_URL for production!');
    return 'http://localhost:18000';
  }
  
  // For web: use same origin (protocol + hostname + port if non-standard)
  // This works for production where API is on same domain
  const { protocol, hostname, port } = window.location;
  
  // Only append port if it's explicitly set and not standard ports (80 for HTTP, 443 for HTTPS)
  const isStandardPort = (port === '80' && protocol === 'http:') || 
                        (port === '443' && protocol === 'https:') || 
                        !port;
  
  if (isStandardPort) {
    return `${protocol}//${hostname}`;
  }
  
  return `${protocol}//${hostname}:${port}`;
};

const API_URL = getApiUrl();

console.log('[API Client] Using API URL:', API_URL);
console.log('[API Client] Running in Capacitor:', isCapacitor());

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  // Timeout for mobile networks
  timeout: 30000,
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Log errors for debugging
    if (!error.response) {
      console.error('[API Client] Network error:', error.message);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
